// src/types/machineMetric.types.ts
var MetricType = /* @__PURE__ */ ((MetricType2) => {
  MetricType2["TEMPERATURE"] = "TEMPERATURE";
  MetricType2["PRESSURE"] = "PRESSURE";
  MetricType2["VIBRATION"] = "VIBRATION";
  MetricType2["SPEED"] = "SPEED";
  MetricType2["ENERGY_CONSUMPTION"] = "ENERGY_CONSUMPTION";
  MetricType2["OUTPUT_RATE"] = "OUTPUT_RATE";
  return MetricType2;
})(MetricType || {});
var MetricTimespan = /* @__PURE__ */ ((MetricTimespan2) => {
  MetricTimespan2["TWENTY_FOUR_HOURS"] = "24h";
  MetricTimespan2["SEVEN_DAYS"] = "7d";
  MetricTimespan2["SIX_MONTHS"] = "6m";
  MetricTimespan2["ONE_YEAR"] = "1y";
  return MetricTimespan2;
})(MetricTimespan || {});

// src/types/notificationChannel.types.ts
var ChannelType = /* @__PURE__ */ ((ChannelType2) => {
  ChannelType2["Email"] = "Email";
  ChannelType2["Teams"] = "Teams";
  ChannelType2["Grouped"] = "Grouped";
  return ChannelType2;
})(ChannelType || {});

// src/enums/permissions.ts
var Permission = /* @__PURE__ */ ((Permission2) => {
  Permission2["READ_USERS"] = "READ_USERS";
  Permission2["WRITE_USERS"] = "WRITE_USERS";
  Permission2["DELETE_USERS"] = "DELETE_USERS";
  Permission2["ADMIN_ACCESS"] = "ADMIN_ACCESS";
  return Permission2;
})(Permission || {});
var UserPermissions = /* @__PURE__ */ ((UserPermissions2) => {
  UserPermissions2["READ_PROFILE"] = "READ_PROFILE";
  UserPermissions2["WRITE_PROFILE"] = "WRITE_PROFILE";
  return UserPermissions2;
})(UserPermissions || {});

// src/enums/errors.ts
var ErrorCode = /* @__PURE__ */ ((ErrorCode2) => {
  ErrorCode2["BAD_USER_INPUT"] = "BAD_USER_INPUT";
  ErrorCode2["VALIDATION_ERROR"] = "VALIDATION_ERROR";
  ErrorCode2["AUTHENTICATION_ERROR"] = "AUTHENTICATION_ERROR";
  ErrorCode2["DB_CONNECTION_ERROR"] = "DB_CONNECTION_ERROR";
  ErrorCode2["INTERNAL_SERVER_ERROR"] = "INTERNAL_SERVER_ERROR";
  ErrorCode2["UNEXPECTED_ERROR"] = "UNEXPECTED_ERROR";
  return ErrorCode2;
})(ErrorCode || {});

// src/enums/auth.ts
var UserRole = /* @__PURE__ */ ((UserRole2) => {
  UserRole2["ADMIN"] = "ADMIN";
  UserRole2["USER"] = "USER";
  UserRole2["VIEWER"] = "VIEWER";
  return UserRole2;
})(UserRole || {});
var AuthProvider = /* @__PURE__ */ ((AuthProvider2) => {
  AuthProvider2["MICROSOFT"] = "MICROSOFT";
  AuthProvider2["LOCAL"] = "LOCAL";
  return AuthProvider2;
})(AuthProvider || {});
var TokenType = /* @__PURE__ */ ((TokenType2) => {
  TokenType2["ACCESS_TOKEN"] = "ACCESS_TOKEN";
  TokenType2["REFRESH_TOKEN"] = "REFRESH_TOKEN";
  TokenType2["ID_TOKEN"] = "ID_TOKEN";
  return TokenType2;
})(TokenType || {});

// src/utils/machineStatus.ts
var EDMS_MACHINE_STATUSES = [
  "offline",
  "ready",
  "ready_with_warning",
  "running",
  "running_with_warning",
  "not_ready",
  "busy"
];
var MACHINE_STATUSES = [
  ...EDMS_MACHINE_STATUSES,
  "unknown"
];
function normalizeStatus(value) {
  const normalized = value?.toLowerCase();
  return EDMS_MACHINE_STATUSES.includes(normalized ?? "") ? normalized : "unknown";
}
function toDate(value) {
  if (!value) return void 0;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? void 0 : date;
}
function readMachineStatus(state) {
  return {
    status: normalizeStatus(state?.state),
    asOf: toDate(state?.timeUtc),
    isSynthetic: state?.isSynthetic === true,
    lastRecordedStatus: state?.lastRecordedState ? normalizeStatus(state.lastRecordedState) : void 0,
    lastRecordedAt: toDate(state?.lastRecordedStateTimeUtc)
  };
}

// src/utils/grafanaUrl.ts
var MACHINE_ID_TOKEN = "{machineId}";
var FALLBACK_MACHINE_VARIABLE = "machine_id";
function buildGrafanaMachineUrl(template, machineIds) {
  if (!template) {
    return { error: "No Grafana dashboard URL is configured" };
  }
  let url;
  try {
    url = new URL(template);
  } catch {
    return {
      error: `Configured Grafana dashboard URL is not a valid absolute URL: ${template}`
    };
  }
  let variable;
  for (const [key, value] of url.searchParams.entries()) {
    if (value === MACHINE_ID_TOKEN) {
      variable = key;
      break;
    }
  }
  let warning;
  if (variable) {
    url.searchParams.delete(variable);
  } else {
    variable = `var-${FALLBACK_MACHINE_VARIABLE}`;
    warning = `Grafana dashboard URL has no ${MACHINE_ID_TOKEN} placeholder, falling back to ${variable}`;
  }
  machineIds.forEach(
    (machineId) => url.searchParams.append(variable, machineId)
  );
  return { url: url.toString(), warning };
}

// src/utils/machineSeries.ts
var MACHINE_SERIES_COLORS = [
  "#006bc9",
  // blue
  "#1baf7a",
  // aqua
  "#4a3aa7",
  // violet
  "#eda100",
  // yellow
  "#e87ba4",
  // magenta
  "#008300"
  // green
];
var MACHINE_SERIES_OVERFLOW_COLOR = "#83888e";
function machineSeriesColor(index) {
  return MACHINE_SERIES_COLORS[index] ?? MACHINE_SERIES_OVERFLOW_COLOR;
}
function machineSeriesColors(series) {
  const colors = /* @__PURE__ */ new Map();
  let slot = 0;
  for (const machine of series) {
    if (machine.points.length > 0) {
      colors.set(machine.id, machineSeriesColor(slot));
      slot += 1;
    }
  }
  return colors;
}
var byTime = (a, b) => a.time - b.time;
function windowMachineSeries(series, from, to) {
  return series.map((machine) => ({
    ...machine,
    points: machine.points.filter((point) => point.time >= from && point.time <= to).sort(byTime)
  }));
}
function machineSeriesExtent(series) {
  let min = Infinity;
  let max = -Infinity;
  for (const machine of series) {
    for (const point of machine.points) {
      if (point.min < min) min = point.min;
      if (point.max > max) max = point.max;
    }
  }
  return min <= max ? { min, max } : void 0;
}
function breachedExtremes(point, thresholds) {
  const breach = {};
  if (thresholds?.maximum !== void 0 && point.max > thresholds.maximum) {
    breach.above = point.max;
  }
  if (thresholds?.minimum !== void 0 && point.min < thresholds.minimum) {
    breach.below = point.min;
  }
  return breach.above === void 0 && breach.below === void 0 ? void 0 : breach;
}
function nearestPointIndex(points, time) {
  if (points.length === 0) return -1;
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const middle = low + high >> 1;
    if (points[middle].time < time) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  if (low > 0 && time - points[low - 1].time <= points[low].time - time) {
    return low - 1;
  }
  return low;
}
function onIntervals(points, isOn, end) {
  const ordered = [...points].sort(byTime);
  const intervals = [];
  let openedAt;
  for (const point of ordered) {
    if (isOn(point)) {
      openedAt ?? (openedAt = point.time);
    } else if (openedAt !== void 0) {
      intervals.push({ from: openedAt, to: point.time });
      openedAt = void 0;
    }
  }
  if (openedAt !== void 0 && end > openedAt) {
    intervals.push({ from: openedAt, to: end });
  }
  return intervals;
}
function typicalGap(points) {
  if (points.length < 2) return void 0;
  const times = points.map((point) => point.time).sort((a, b) => a - b);
  const gaps = times.slice(1).map((time, index) => time - times[index]).filter((gap) => gap > 0).sort((a, b) => a - b);
  return gaps.length > 0 ? gaps[Math.floor(gaps.length / 2)] : void 0;
}

// src/utils/signalCategory.ts
var EDMS_MACHINE_ALARMS_CATEGORY = "machine-alarms";
function edmsSignalCategory(category) {
  if (typeof category !== "string") return category;
  return category.trim().toLowerCase() === "machinealarm" ? EDMS_MACHINE_ALARMS_CATEGORY : category;
}

// src/utils/index.ts
var isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
var isNotEmpty = (value) => {
  return value !== null && value !== void 0 && value.trim().length > 0;
};
export {
  AuthProvider,
  ChannelType,
  EDMS_MACHINE_ALARMS_CATEGORY,
  EDMS_MACHINE_STATUSES,
  ErrorCode,
  MACHINE_ID_TOKEN,
  MACHINE_SERIES_COLORS,
  MACHINE_SERIES_OVERFLOW_COLOR,
  MACHINE_STATUSES,
  MetricTimespan,
  MetricType,
  Permission,
  TokenType,
  UserPermissions,
  UserRole,
  breachedExtremes,
  buildGrafanaMachineUrl,
  edmsSignalCategory,
  isNotEmpty,
  isValidEmail,
  machineSeriesColor,
  machineSeriesColors,
  machineSeriesExtent,
  nearestPointIndex,
  onIntervals,
  readMachineStatus,
  typicalGap,
  windowMachineSeries
};
